// Accept Invite — landing page for the email link sent by the
// invite-user edge function. Supabase JS auto-detects the hash params
// (#access_token=…&type=invite) and creates a session before this page
// renders, so we just need to show a "set your password" form and call
// supabase.auth.updateUser({ password }).
//
// Also handles the password-recovery flow (#type=recovery) since the UX
// is identical: confirm a password, save, redirect home.
//
// HAND-OVER LINKS (docs/specs/no-mailbox-logins.md §3.4). A link an admin
// hands to a no-mailbox login — or gets back when the mailer failed — is the
// token_hash form: ?token_hash=…&type=invite|recovery&login=…. Nothing
// happens on load for those: WhatsApp / Teams previews and Safe-Links
// prefetch URLs, and GoTrue's action_link burns the one-time token on that
// prefetch. We render an interstitial and exchange the token only on the
// button tap, over REST (verifyTokenHashDirect — never supabase.auth.verifyOtp,
// which takes the navigator.locks lock this page already avoids for the
// save), store the session and reload: the hash flow below then runs as-is.

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { CheckCircle, KeyRound, AlertTriangle } from 'lucide-react'
import { POLICY_VERSION, POLICY_VERSION_LABEL } from '@/lib/popia'
import { TERMS_VERSION, TERMS_VERSION_LABEL, acceptsTermsForBusiness } from '@/lib/terms'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { withTimeout } from '@/lib/saveTimeout'
import { verifyTokenHashDirect, type TokenHashType } from '@/lib/authDirect'
import { BrandLoader } from '@/components/BrandLoader'

// A hand-over link's query, read once at mount. Absent for the mail flow.
function readHandover(): { tokenHash: string; type: TokenHashType; login: string | null } | null {
  const q = new URLSearchParams(window.location.search)
  const tokenHash = q.get('token_hash')
  const type = q.get('type')
  if (!tokenHash || (type !== 'invite' && type !== 'recovery')) return null
  return { tokenHash, type, login: q.get('login') }
}

export default function AcceptInvitePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  // Terms only for an administrator of the business that invited them
  // (Jason's ruling, 6 Oct — acceptsTermsForBusiness); everyone else ticks
  // the Privacy Policy alone. The same predicate as ConsentGate, so the form
  // and the gate can never disagree about who is asked.
  const { data: me } = useCurrentUser()
  const asksTerms = acceptsTermsForBusiness(me?.tenant_role)
  const [name,    setName]    = useState<string>('')
  const [pwd1,    setPwd1]    = useState('')
  const [pwd2,    setPwd2]    = useState('')
  const [error,   setError]   = useState<string | null>(null)
  const [saving,  setSaving]  = useState(false)
  const [done,    setDone]    = useState(false)
  // Wait for Supabase to finish processing the hash before deciding
  // whether the link is valid. Otherwise we'd render the "invalid link"
  // state in the split-second between mount and session-load.
  const [ready,   setReady]   = useState(false)
  const [hasSession, setHasSession] = useState(false)
  // Cache the recovery access_token at hash-load time so the Save flow
  // can hit /auth/v1/user directly. supabase.auth.updateUser() goes
  // through navigator.locks and contends with the background banners /
  // session-refresh that fire on first paint — the form would hang on
  // "Saving…" indefinitely. Same bypass pattern as the tenant-switcher
  // (Dashboard.tsx).
  const [accessToken, setAccessToken] = useState<string | null>(null)
  // POPIA — the invite-accept page is the first contact with the user
  // as an account holder, so this is the right moment to capture
  // consent to the privacy policy. We refuse to submit unless ticked;
  // the consent is recorded via record_user_consent RPC after the
  // password save succeeds.
  const [agreed, setAgreed] = useState(false)
  // The tick names the documents the label showed WHEN it was ticked. If the
  // role arrives after the tick and the label gains (or loses) the Terms, the
  // tick no longer matches what was read — clear it, so nobody is recorded as
  // accepting a document that was not on screen when they agreed.
  useEffect(() => { setAgreed(false) }, [asksTerms])
  // Hand-over link state (token_hash form). `linkDead` = GoTrue refused the
  // token (used / expired / unknown) — the "ask your admin" card, not a retry.
  const [handover]  = useState(readHandover)
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [linkDead, setLinkDead] = useState(false)

  async function continueHandover() {
    if (!handover || verifying) return
    setVerifying(true)
    setVerifyError(null)
    const r = await verifyTokenHashDirect(handover.tokenHash, handover.type)
    if (r.ok) {
      // Session is in localStorage. Drop the one-time token from the URL so
      // the reload (and a later refresh) cannot re-run this branch, then let
      // supabase-js rehydrate with no lock state — the hash flow takes over.
      window.history.replaceState(null, '', '/accept-invite')
      window.location.reload()
      return
    }
    setVerifying(false)
    if (r.expired) setLinkDead(true)
    else setVerifyError(r.error)
  }

  useEffect(() => {
    let cancelled = false
    // Either we already have a session (Supabase auto-loaded it from the
    // hash) or one will arrive shortly via onAuthStateChange.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return
      if (data.session) {
        setHasSession(true)
        setAccessToken(data.session.access_token)
        setName((data.session.user.user_metadata?.invited_name as string) ?? '')
      }
      setReady(true)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      if (cancelled) return
      if (s) {
        setHasSession(true)
        setAccessToken(s.access_token)
        setName((s.user.user_metadata?.invited_name as string) ?? '')
      }
      setReady(true)
    })
    return () => { cancelled = true; subscription.unsubscribe() }
  }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (pwd1.length < 8)         { setError('Password must be at least 8 characters'); return }
    if (pwd1 !== pwd2)           { setError('Passwords don\'t match'); return }
    if (!accessToken)            { setError('Session not ready — refresh the page and click the email link again.'); return }
    if (!agreed)                 { setError(asksTerms
                                     ? 'Please review and accept the Terms of Service and the Privacy Policy to continue.'
                                     : 'Please review and accept the Privacy Policy to continue.'); return }
    setSaving(true)
    try {
      // Direct REST PUT to /auth/v1/user — bypasses supabase-js auth lock.
      const url     = import.meta.env.VITE_SUPABASE_URL  as string
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string
      const resp = await fetch(`${url}/auth/v1/user`, {
        method:  'PUT',
        headers: {
          'Content-Type':  'application/json',
          'apikey':        anonKey,
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ password: pwd1 }),
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => null) as { msg?: string; error_description?: string } | null
        throw new Error(body?.msg ?? body?.error_description ?? `Password update failed (${resp.status})`)
      }
      // Record POPIA consent immediately after the password is set. We
      // do this via the supabase-js RPC (now that the session is
      // confirmed valid by the PUT above). Failure is logged but does
      // not block the user — ConsentGate stands aside on THIS page only
      // (it takes the acceptance itself), so the next page's gate will
      // catch a missing consent and re-prompt; the worst case is they
      // see the gate once on landing. We deliberately don't send
      // an IP from the client — Supabase records `request.headers`
      // server-side if a future hardening step needs it.
      // The tick-box covers what its label named: the Privacy Policy, and —
      // for an administrator only — the Terms on the business's behalf. Each
      // is its own top-level key in users.consents, so a later bump to one
      // does not disturb the other's evidence trail.
      try {
        const versions = asksTerms ? [POLICY_VERSION, TERMS_VERSION] : [POLICY_VERSION]
        for (const version of versions) {
          await withTimeout(supabase.rpc('record_user_consent', {
            p_policy_version: version,
            p_context:        'invite_accept',
            p_ip:             null,
            p_user_agent:     navigator.userAgent.slice(0, 200),
          }), `AcceptInvite: record_user_consent ${version}`)
        }
      } catch (consentErr) {
        console.warn('AcceptInvite: record_user_consent failed (ConsentGate will retry)', consentErr)
      }
      // Refresh the cached profile during the 1.5 s below, so the gate on '/'
      // reads the acceptance just written instead of the consent-less copy it
      // loaded with — otherwise the person who just ticked the box is asked
      // again on landing. Not awaited: a slow read must not hold the hand-off.
      void qc.invalidateQueries({ queryKey: ['me'] })
      setSaving(false)
      setDone(true)
      // Brief celebration, then home. The session is already valid.
      setTimeout(() => navigate('/', { replace: true }), 1500)
    } catch (e) {
      setSaving(false)
      setError((e as Error).message)
    }
  }

  if (!ready) {
    return (
      <div className="h-full flex items-center justify-center">
        <BrandLoader className="w-10 h-10" />
      </div>
    )
  }

  // A hand-over link in the URL OUTRANKS any session already on this device.
  // The admin often opens the link on their own signed-in tablet with the
  // person standing there (spec §2 step 3); falling through to the form below
  // would have that person set the ADMIN's password. Nothing is exchanged
  // until the tap either, so a WhatsApp/Safe-Links prefetch burns nothing.
  if (handover && !linkDead) {
    return (
      <HandoverInterstitial
        type={handover.type}
        login={handover.login}
        otherSessionPresent={hasSession}
        verifying={verifying}
        error={verifyError}
        onContinue={continueHandover}
      />
    )
  }

  // A dead hand-over link, or the mail flow arriving with no session at all:
  // the link was bad, expired, or already consumed. `linkDead` is checked
  // ahead of hasSession for the same reason as the branch above.
  if (linkDead || !hasSession) {
    return (
      <div className="min-h-full flex items-center justify-center p-6 surface-sunk">
        <div className="w-full max-w-sm card p-6 text-center space-y-3">
          <AlertTriangle size={32} className="text-amber-400 mx-auto" />
          <h1 className="text-lg font-semibold text-fg">{handover?.type === 'recovery' ? 'Reset link not valid' : 'Invite link not valid'}</h1>
          <p className="text-sm text-fg-muted">
            This link has expired or was already used. Ask your admin for a new one
            {handover ? ' — they can make one from your row on the Members page.' : '.'}
          </p>
          <button onClick={() => navigate('/login')} className="btn-secondary btn-sm">
            Go to sign-in
          </button>
        </div>
      </div>
    )
  }

  if (done) {
    return (
      <div className="min-h-full flex items-center justify-center p-6 surface-sunk">
        <div className="w-full max-w-sm card p-6 text-center space-y-3">
          <CheckCircle size={36} className="text-emerald-400 mx-auto" />
          <h1 className="text-lg font-semibold text-fg">Welcome aboard</h1>
          <p className="text-sm text-fg-muted">Password saved — taking you in…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full flex flex-col items-center justify-center p-6 surface-sunk">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 mb-3">
            <KeyRound size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-fg">
            {name ? `Hi ${name.split(' ')[0]}` : 'Welcome'}
          </h1>
          <p className="text-fg-muted mt-1">Set a password to finish setting up your account</p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="label">New password</label>
            <input type="password" className="field" minLength={8}
              placeholder="At least 8 characters" required autoComplete="new-password"
              value={pwd1} onChange={e => setPwd1(e.target.value)} />
          </div>
          <div>
            <label className="label">Confirm password</label>
            <input type="password" className="field" minLength={8}
              placeholder="Re-enter the same password" required autoComplete="new-password"
              value={pwd2} onChange={e => setPwd2(e.target.value)} />
          </div>

          {/* POPIA consent — must be ticked before we'll create the
              account. Renders BEFORE the error block so the operator
              sees the consent state right next to the submit button. */}
          <label className="flex items-start gap-2 text-xs text-fg-secondary cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5 shrink-0"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
            />
            <span>
              I have read and agree to the{' '}
              {asksTerms && <>
                <Link to="/terms" target="_blank" rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline">
                  Terms of Service
                </Link>{' '}
                <span className="text-muted">({TERMS_VERSION_LABEL})</span>{' '}
                on behalf of my business, and the{' '}
              </>}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline">
                Privacy Policy
              </Link>{' '}
              <span className="text-fg-muted">({POLICY_VERSION_LABEL})</span>.
            </span>
          </label>

          {error && (
            <div className="rounded-xl bg-red-900/50 border border-red-700 px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          <button type="submit" disabled={saving || !agreed} className="btn-primary w-full">
            {saving ? 'Saving…' : 'Save password & continue'}
          </button>
        </form>
      </div>
    </div>
  )
}


/**
 * The screen a handed-over link lands on before anything is exchanged
 * (docs/specs/no-mailbox-logins.md §3.4). Its whole job is to do NOTHING
 * until the person taps: a link preview or Safe-Links prefetch that renders
 * this has burnt no token.
 */
function HandoverInterstitial({ type, login, otherSessionPresent, verifying, error, onContinue }: {
  type: TokenHashType
  login: string | null
  /** Someone is already signed in here — continuing signs them out. */
  otherSessionPresent: boolean
  verifying: boolean
  error: string | null
  onContinue: () => void
}) {
  const isInvite = type === 'invite'
  return (
    <div className="min-h-full flex flex-col items-center justify-center p-6 surface-sunk">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 mb-3">
            <KeyRound size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-fg">{isInvite ? "You're invited" : 'Reset your password'}</h1>
          <p className="text-fg-muted mt-1">
            {isInvite ? 'Continue to set a password for your login.' : 'Continue to choose a new password.'}
          </p>
          {login && (
            <p className="text-xs text-fg-muted mt-3">
              Your login name is <span className="font-mono text-fg-secondary break-all">{login}</span>.
              You will use it to sign in — keep it.
            </p>
          )}
          {otherSessionPresent && (
            <p className="text-xs text-amber-300 mt-3">
              Someone else is signed in on this device. Continuing signs them out.
            </p>
          )}
        </div>
        {error && (
          <div className="rounded-xl bg-red-900/50 border border-red-700 px-4 py-3 text-sm text-red-300 mb-4">
            {error}
          </div>
        )}
        <button type="button" onClick={onContinue} disabled={verifying} className="btn-primary w-full">
          {verifying ? 'Continuing…' : (isInvite ? 'Continue to set my password' : 'Continue')}
        </button>
      </div>
    </div>
  )
}
